/**
 * The small pieces the Fravær tabs and the absence drawer share, so a category, a status and the
 * filters look the same wherever they appear.
 */
import type { ApiAbsence } from '@/api/data';
import { T } from '@/i18n';
import { avTone, initials } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { Status } from '@/ui/page';
import { CATEGORIES, type Category, catInfo } from './category';
import { type Filter, NO_FILTER } from './filters';

/** The category's icon in its colour; the text beside it names the type. */
export const CatIcon = ({ cat }: { cat: Category }) => (
  <span className={`cati tn-${catInfo(cat).tone}`} aria-hidden="true">
    <Icon name={catInfo(cat).icon} />
  </span>
);

export function StatusChip({ status, cancel }: { status: ApiAbsence['status']; cancel?: boolean }) {
  if (status === 'accepted' && cancel) return <Status tone="warn">{T('Ønsker avlysning')}</Status>;
  if (status === 'pending') return <Status tone="warn">{T('Venter på svar')}</Status>;
  if (status === 'accepted') return <Status tone="ok">{T('Innvilget')}</Status>;
  return <Status tone="dgr">{T('Avslått')}</Status>;
}

/** Avatar, name and a second line (the team, or nothing). */
export const Person = ({ id, name, sub }: { id: number; name: string; sub?: string | undefined }) => (
  <span className="fp mwho">
    <span className={`av ${avTone(id)}`}>{initials(name)}</span>
    <span>
      <b>{name}</b>
      {sub ? <small>{sub}</small> : null}
    </span>
  </span>
);

/**
 * Search, Team and Fraværstype (manager redesign, 2026-10-09). On a wide screen one toolbar row,
 * the selects labelled; on a phone the search stays and the two selects fold into one Filter group
 * with the count of what is active and a reset, so the list starts high on the screen. `open` lives
 * on the page so the group stays as it was across the tabs.
 */
export function FilterBar({
  value,
  onChange,
  teams,
  placeholder,
  open,
  onOpen,
  children,
}: {
  value: Filter;
  onChange: (f: Filter) => void;
  teams: string[];
  placeholder: string;
  open: boolean;
  onOpen: (open: boolean) => void;
  /** what goes before the search on the same row (the view, the period, the year) */
  children?: React.ReactNode;
}) {
  const active = [
    value.team && `${T('Team')}: ${value.team}`,
    value.cat && `${T('Type')}: ${T(catInfo(value.cat).label)}`,
  ].filter(Boolean) as string[];
  return (
    <div className="mbar fvbar" data-open={open}>
      {children}
      <label className="fsrch">
        <Icon name="search" />
        <input
          className="inp fsearch"
          type="search"
          aria-label={T('Søk')}
          placeholder={placeholder}
          value={value.q}
          onChange={(e) => onChange({ ...value, q: e.target.value })}
        />
      </label>
      <span className="sp" />
      <button
        type="button"
        className="btn ftog"
        aria-expanded={open}
        aria-controls="fravaer-filters"
        aria-label={`${T('Filter')}, ${active.length === 1 ? T('1 aktivt filter') : T('{n} aktive filtre', { n: active.length })}`}
        onClick={() => onOpen(!open)}
      >
        <Icon name="sliders" />
        {T('Filter')}
        {active.length > 0 && <span className="mtc">{active.length}</span>}
      </button>
      <div className="fsel" id="fravaer-filters">
        {teams.length > 0 && (
          <label className="mf">
            {T('Team')}
            <select
              className="inp"
              aria-label={T('Team')}
              value={value.team}
              onChange={(e) => onChange({ ...value, team: e.target.value })}
            >
              <option value="">{T('Alle team')}</option>
              {teams.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </label>
        )}
        <label className="mf">
          {T('Fraværstype')}
          <select
            className="inp"
            aria-label={T('Fraværstype')}
            value={value.cat}
            onChange={(e) => onChange({ ...value, cat: e.target.value as Filter['cat'] })}
          >
            <option value="">{T('Alle typer')}</option>
            {CATEGORIES.map((c) => (
              <option key={c.key} value={c.key}>
                {T(c.label)}
              </option>
            ))}
          </select>
        </label>
      </div>
      {active.length > 0 && (
        <div className="fsum">
          <ul aria-label={T('Aktive filtre')}>
            {active.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
          <button
            type="button"
            className="mlink"
            aria-label={T('Nullstill fraværsfiltre')}
            onClick={() => onChange(NO_FILTER)}
          >
            {T('Nullstill')}
          </button>
        </div>
      )}
    </div>
  );
}
